import { useState } from 'react';
import {
  Building2,
  User,
  FileText,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Briefcase,
  UserCircle,
  Building,
  Landmark,
  Loader2,
  Pencil,
  Warehouse,
} from 'lucide-react';
import { useOnboarding, useSaveOnboarding, useSubmitOnboarding, type OnboardingData } from '@/hooks/use-onboarding';
import { useAuthStore } from '@/stores/auth-store';
import { useLocations } from '@/hooks/use-locations';
import { cn } from '@/lib/utils';
import { US_STATES, getStateName } from '@/lib/constants';
import { validateDriversLicense, sanitizeDriversLicense, validatePhone, formatPhone } from '@/lib/validators';
import { PhoneInput } from '@/components/ui/phone-input';
import { DateInput } from '@/components/ui/date-input';

const inputBase = 'h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:ring-1';
const inputNormal = `${inputBase} border-input focus:border-primary focus:ring-primary`;
const inputError = `${inputBase} border-red-500 focus:border-red-500 focus:ring-red-500`;

type FieldErrors = Record<string, string>;

/** Reusable inline error message for form fields */
function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1 text-xs text-red-500" role="alert">
      {message}
    </p>
  );
}

/** Returns the correct input class based on whether a field has an error */
function fc(fieldErrors: FieldErrors, field: string): string {
  return fieldErrors[field] ? inputError : inputNormal;
}

const STEPS = [
  { key: 'business_type', label: 'Business Type', icon: Briefcase },
  { key: 'business_details', label: 'Business Details', icon: Building },
  { key: 'banking', label: 'Banking Details', icon: Landmark },
  { key: 'person', label: 'Representative', icon: UserCircle },
  { key: 'review', label: 'Review & Submit', icon: FileText },
] as const;

type StepKey = (typeof STEPS)[number]['key'];

const STEP_ORDER: StepKey[] = ['business_type', 'business_details', 'banking', 'person', 'review'];

function getStepIndex(step: StepKey): number {
  return STEP_ORDER.indexOf(step);
}

const INDUSTRIES = [
  'Restaurant',
  'Grocery / Super Market',
  'Beauty & Salon',
  'IT Services',
  'Accounting Services',
  'Jewelery Shop',
  'Healthcare',
  'E-Commerce',
  'Others',
];

// ─── Step Components ────────────────────────────────────────────────

function BusinessTypeStep({
  data,
  onChange,
}: {
  data: Partial<OnboardingData>;
  onChange: (field: string, value: string) => void;
}) {
  const types = [
    {
      value: 'company',
      label: 'Company',
      description: 'Corporation, LLC, or Partnership',
      icon: Building2,
    },
    {
      value: 'individual',
      label: 'Individual',
      description: 'Sole Proprietorship or Single-Member LLC',
      icon: User,
    },
    {
      value: 'non_profit',
      label: 'Non-Profit',
      description: 'Tax-exempt non-profit organization',
      icon: Building,
    },
    {
      value: 'warehouse',
      label: 'Warehouse',
      description: 'Warehouse, vendor, manufacturer, distributors etc.',
      icon: Warehouse,
    },
  ];

  return (
    <div>
      <h2 className="mb-2 text-xl font-semibold text-foreground">What type of business do you have?</h2>
      <p className="mb-6 text-sm text-muted-foreground">
        This helps us understand your business structure and compliance requirements.
      </p>
      <div className="space-y-3">
        {types.map((type) => {
          const Icon = type.icon;
          const isSelected = data.businessType === type.value;
          return (
            <button
              key={type.value}
              onClick={() => onChange('businessType', type.value)}
              className={cn(
                'flex w-full items-center gap-4 rounded-xl border-2 p-4 text-left transition-all',
                isSelected
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'border-border bg-white hover:border-primary/40 hover:bg-primary/5',
              )}
            >
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-lg',
                  isSelected ? 'bg-primary text-white' : 'bg-muted text-muted-foreground',
                )}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium text-foreground">{type.label}</p>
                <p className="text-sm text-muted-foreground">{type.description}</p>
              </div>
              {isSelected && <CheckCircle2 className="ml-auto h-5 w-5 text-primary" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BusinessDetailsStep({
  data,
  onChange,
  fieldErrors,
}: {
  data: Partial<OnboardingData>;
  onChange: (field: string, value: string) => void;
  fieldErrors: FieldErrors;
}) {
  return (
    <div>
      <h2 className="mb-2 text-xl font-semibold text-foreground">Business Details</h2>
      <p className="mb-6 text-sm text-muted-foreground">Provide your legal business information for verification.</p>
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            Legal Business Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={data.legalBusinessName || ''}
            onChange={(e) => onChange('legalBusinessName', e.target.value)}
            className={fc(fieldErrors, 'legalBusinessName')}
            placeholder="Acme Corporation LLC"
            aria-invalid={!!fieldErrors.legalBusinessName}
          />
          <FieldError message={fieldErrors.legalBusinessName} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              EIN <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.ein || ''}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                const formatted = digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
                onChange('ein', formatted);
              }}
              className={fc(fieldErrors, 'ein')}
              placeholder="XX-XXXXXXX"
              maxLength={10}
              inputMode="numeric"
              aria-invalid={!!fieldErrors.ein}
            />
            <FieldError message={fieldErrors.ein} />
            {!fieldErrors.ein && data.businessType === 'individual' && (
              <p className="mt-1 text-xs text-muted-foreground">If EIN not available, you can enter SSN Number</p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Industry</label>
            <select
              value={data.industry || ''}
              onChange={(e) => {
                onChange('industry', e.target.value);
                if (e.target.value !== 'Others') {
                  onChange('otherIndustry', '');
                }
              }}
              className={fc(fieldErrors, 'industry')}
              aria-invalid={!!fieldErrors.industry}
            >
              <option value="">Select Industry</option>
              {INDUSTRIES.map((ind) => (
                <option key={ind} value={ind}>
                  {ind}
                </option>
              ))}
            </select>
            <FieldError message={fieldErrors.industry} />
          </div>
        </div>

        {/* Conditional "Others" input */}
        {data.industry === 'Others' && (
          <div className="mt-4 transition-all duration-200">
            <label className="mb-1 block text-sm font-medium text-foreground">
              Please specify your industry <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.otherIndustry || ''}
              onChange={(e) => onChange('otherIndustry', e.target.value)}
              className={fc(fieldErrors, 'otherIndustry')}
              placeholder="e.g. Logistics, Real Estate"
              autoFocus
              aria-invalid={!!fieldErrors.otherIndustry}
            />
            <FieldError message={fieldErrors.otherIndustry} />
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Business Website</label>
            <input
              type="url"
              value={data.businessWebsite || ''}
              onChange={(e) => onChange('businessWebsite', e.target.value)}
              className={fc(fieldErrors, 'businessWebsite')}
              placeholder="example.com or https://example.com"
              aria-invalid={!!fieldErrors.businessWebsite}
            />
            <FieldError message={fieldErrors.businessWebsite} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Business Phone</label>
            <PhoneInput
              value={data.businessPhone || ''}
              onValueChange={(digits) => onChange('businessPhone', digits)}
              className={fc(fieldErrors, 'businessPhone')}
              placeholder="123-456-7890"
              aria-invalid={!!fieldErrors.businessPhone}
            />
            <FieldError message={fieldErrors.businessPhone} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">DBA (Doing Business As)</label>
            <input
              type="text"
              value={data.dbaName || ''}
              onChange={(e) => onChange('dbaName', e.target.value.replace(/[^A-Za-z\s]/g, ''))}
              className={inputNormal}
              placeholder="Only if different than legal"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Business Started Date</label>
            <DateInput
              value={data.businessStartedDate || ''}
              onChange={(e) => onChange('businessStartedDate', e.target.value)}
              max={(() => {
                const d = new Date();
                d.setDate(d.getDate() - 1);
                return d.toISOString().split('T')[0];
              })()}
              className={fc(fieldErrors, 'businessStartedDate')}
              aria-invalid={!!fieldErrors.businessStartedDate}
            />
            <FieldError message={fieldErrors.businessStartedDate} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Average Monthly Sales Volume <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.averageMonthlySalesVolume || ''}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.]/g, '');
                const parts = value.split('.');
                const sanitized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                onChange('averageMonthlySalesVolume', sanitized);
              }}
              className={fc(fieldErrors, 'averageMonthlySalesVolume')}
              placeholder="50000.00"
              inputMode="decimal"
              aria-invalid={!!fieldErrors.averageMonthlySalesVolume}
            />
            <FieldError message={fieldErrors.averageMonthlySalesVolume} />
            {!fieldErrors.averageMonthlySalesVolume && (
              <p className="mt-1 text-xs text-muted-foreground">
                Combined Estimation (MC / Visa / Discover / Amex / Debit / Pin debit / EBT)
              </p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Average Sale Amount Per Transaction
            </label>
            <input
              type="text"
              value={data.averageSaleAmountPerTransaction || ''}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.]/g, '');
                const parts = value.split('.');
                const sanitized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                onChange('averageSaleAmountPerTransaction', sanitized);
              }}
              className={fc(fieldErrors, 'averageSaleAmountPerTransaction')}
              placeholder="20.00"
              inputMode="decimal"
              aria-invalid={!!fieldErrors.averageSaleAmountPerTransaction}
            />
            <FieldError message={fieldErrors.averageSaleAmountPerTransaction} />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">Street Address</label>
          <input
            type="text"
            value={data.businessStreet || ''}
            onChange={(e) => onChange('businessStreet', e.target.value)}
            className={inputNormal}
            placeholder="123 Main St, Suite 100"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">City</label>
            <input
              type="text"
              value={data.businessCity || ''}
              onChange={(e) => onChange('businessCity', e.target.value.replace(/[^A-Za-z\s]/g, ''))}
              className={inputNormal}
              placeholder="San Francisco"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">State</label>
            <select
              value={data.businessState || ''}
              onChange={(e) => onChange('businessState', e.target.value)}
              className={inputNormal}
            >
              <option value="">Select</option>
              {US_STATES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Zip Code</label>
            <input
              type="text"
              value={data.businessZip || ''}
              onChange={(e) => onChange('businessZip', e.target.value.replace(/\D/g, '').slice(0, 5))}
              className={inputNormal}
              placeholder="94102"
              maxLength={5}
              inputMode="numeric"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Country</label>
            <input
              type="text"
              value={data.businessCountry || 'United States'}
              readOnly
              className={`${inputNormal} bg-muted cursor-not-allowed`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function BankingDetailsStep({
  data,
  onChange,
  fieldErrors,
}: {
  data: Partial<OnboardingData>;
  onChange: (field: string, value: string) => void;
  fieldErrors: FieldErrors;
}) {
  return (
    <div>
      <h2 className="mb-2 text-xl font-semibold text-foreground">Banking Details</h2>
      <p className="mb-6 text-sm text-muted-foreground">Provide your bank information for verification.</p>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Routing Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.routingNumber || ''}
              onChange={(e) => onChange('routingNumber', e.target.value.replace(/\D/g, '').slice(0, 9))}
              className={fc(fieldErrors, 'routingNumber')}
              placeholder="123456789"
              maxLength={9}
              inputMode="numeric"
              aria-invalid={!!fieldErrors.routingNumber}
            />
            <FieldError message={fieldErrors.routingNumber} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Account Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.accountNumber || ''}
              onChange={(e) => onChange('accountNumber', e.target.value.replace(/\D/g, '').slice(0, 17))}
              className={fc(fieldErrors, 'accountNumber')}
              placeholder="000123456789"
              maxLength={17}
              inputMode="numeric"
              aria-invalid={!!fieldErrors.accountNumber}
            />
            <FieldError message={fieldErrors.accountNumber} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Bank Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.bankName || ''}
              onChange={(e) => onChange('bankName', e.target.value.replace(/[^A-Za-z\s]/g, ''))}
              className={fc(fieldErrors, 'bankName')}
              placeholder="First National Bank"
              aria-invalid={!!fieldErrors.bankName}
            />
            <FieldError message={fieldErrors.bankName} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Bank Representative Name</label>
            <input
              type="text"
              value={data.bankRepresentativeName || ''}
              onChange={(e) => onChange('bankRepresentativeName', e.target.value.replace(/[^A-Za-z\s]/g, ''))}
              className={inputNormal}
              placeholder="Jane Smith"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function PersonStep({
  data,
  onChange,
  fieldErrors,
}: {
  data: Partial<OnboardingData>;
  onChange: (field: string, value: string) => void;
  fieldErrors: FieldErrors;
}) {
  return (
    <div>
      <h2 className="mb-2 text-xl font-semibold text-foreground">Business Representative</h2>
      <p className="mb-6 text-sm text-muted-foreground">
        Provide information about the person who has authority to act on behalf of the business.
      </p>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.repFirstName || ''}
              onChange={(e) => onChange('repFirstName', e.target.value.replace(/[^A-Za-z\s]/g, ''))}
              className={fc(fieldErrors, 'repFirstName')}
              placeholder="John"
              aria-invalid={!!fieldErrors.repFirstName}
            />
            <FieldError message={fieldErrors.repFirstName} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={data.repLastName || ''}
              onChange={(e) => onChange('repLastName', e.target.value.replace(/[^A-Za-z\s]/g, ''))}
              className={fc(fieldErrors, 'repLastName')}
              placeholder="Doe"
              aria-invalid={!!fieldErrors.repLastName}
            />
            <FieldError message={fieldErrors.repLastName} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <DateInput
              value={data.repDob || ''}
              onChange={(e) => onChange('repDob', e.target.value)}
              max={(() => {
                const d = new Date();
                d.setDate(d.getDate() - 1);
                return d.toISOString().split('T')[0];
              })()}
              className={fc(fieldErrors, 'repDob')}
              aria-invalid={!!fieldErrors.repDob}
            />
            <FieldError message={fieldErrors.repDob} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              SSN Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              maxLength={11}
              value={data.repSsnLast4 || ''}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                let formatted = digits;
                if (digits.length > 5) {
                  formatted = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
                } else if (digits.length > 3) {
                  formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
                }
                onChange('repSsnLast4', formatted);
              }}
              className={fc(fieldErrors, 'repSsnLast4')}
              placeholder="XXX-XX-XXXX"
              inputMode="numeric"
              aria-invalid={!!fieldErrors.repSsnLast4}
            />
            <FieldError message={fieldErrors.repSsnLast4} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={data.repEmail || ''}
              onChange={(e) => onChange('repEmail', e.target.value)}
              className={fc(fieldErrors, 'repEmail')}
              placeholder="john@example.com"
              aria-invalid={!!fieldErrors.repEmail}
            />
            <FieldError message={fieldErrors.repEmail} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Phone <span className="text-red-500">*</span>
            </label>
            <PhoneInput
              value={data.repPhone || ''}
              onValueChange={(digits) => onChange('repPhone', digits)}
              className={fc(fieldErrors, 'repPhone')}
              placeholder="123-456-7890"
              aria-invalid={!!fieldErrors.repPhone}
            />
            <FieldError message={fieldErrors.repPhone} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Job Title</label>
            <input
              type="text"
              value={data.repJobTitle || ''}
              onChange={(e) => onChange('repJobTitle', e.target.value.replace(/[^A-Za-z\s]/g, ''))}
              className={inputNormal}
              placeholder="CEO, Owner, Director, etc."
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Ownership Percentage</label>
            <div className="relative">
              <input
                type="text"
                value={data.ownershipPercentage || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  const num = Number(value);
                  if (value === '' || (num >= 0 && num <= 100)) {
                    onChange('ownershipPercentage', value);
                  }
                }}
                className={`${inputNormal} pr-8`}
                placeholder="ex: 50"
                maxLength={3}
                inputMode="numeric"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
                %
              </span>
            </div>
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">Street Address</label>
          <input
            type="text"
            value={data.repStreet || ''}
            onChange={(e) => onChange('repStreet', e.target.value)}
            className={inputNormal}
            placeholder="456 Oak Ave"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">City</label>
            <input
              type="text"
              value={data.repCity || ''}
              onChange={(e) => onChange('repCity', e.target.value.replace(/[^A-Za-z\s]/g, ''))}
              className={inputNormal}
              placeholder="San Francisco"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">State</label>
            <select
              value={data.repState || ''}
              onChange={(e) => onChange('repState', e.target.value)}
              className={inputNormal}
            >
              <option value="">Select</option>
              {US_STATES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Zip Code</label>
            <input
              type="text"
              value={data.repZip || ''}
              onChange={(e) => onChange('repZip', e.target.value.replace(/\D/g, '').slice(0, 5))}
              className={inputNormal}
              placeholder="94102"
              maxLength={5}
              inputMode="numeric"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Country</label>
            <input
              type="text"
              value={data.repCountry || 'United States'}
              readOnly
              className={`${inputNormal} bg-muted cursor-not-allowed`}
            />
          </div>
        </div>

        {/* Driver's License */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Driver&apos;s License Number</label>
            <input
              type="text"
              value={data.repDriverLicenseNumber || ''}
              onChange={(e) => {
                onChange('repDriverLicenseNumber', sanitizeDriversLicense(e.target.value));
              }}
              className={fc(fieldErrors, 'repDriverLicenseNumber')}
              placeholder="e.g. D1234567"
              aria-invalid={!!fieldErrors.repDriverLicenseNumber}
            />
            <FieldError message={fieldErrors.repDriverLicenseNumber} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Driver&apos;s License State</label>
            <select
              value={data.repDriverLicenseState || ''}
              onChange={(e) => onChange('repDriverLicenseState', e.target.value)}
              className={inputNormal}
            >
              <option value="">Select</option>
              {US_STATES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">DL&apos;s License Expiry Date</label>
            <DateInput
              value={data.repDriverLicenseExpiry || ''}
              onChange={(e) => onChange('repDriverLicenseExpiry', e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className={fc(fieldErrors, 'repDriverLicenseExpiry')}
              aria-invalid={!!fieldErrors.repDriverLicenseExpiry}
            />
            <FieldError message={fieldErrors.repDriverLicenseExpiry} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewStep({ data, onEdit }: { data: Partial<OnboardingData>; onEdit: (step: StepKey) => void }) {
  const businessTypeLabels: Record<string, string> = {
    company: 'Company',
    individual: 'Individual',
    non_profit: 'Non-profit',
    warehouse: 'Warehouse',
  };

  return (
    <div>
      <h2 className="mb-2 text-xl font-semibold text-foreground">Review your Information</h2>
      <p className="mb-6 text-sm text-muted-foreground">
        Please review all details below before submitting your application.
      </p>
      <div className="space-y-4">
        {/* Business Type */}
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-medium text-foreground">Business Type</h3>
            <button
              onClick={() => onEdit('business_type')}
              className="flex items-center gap-1 text-sm text-primary hover:underline"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </button>
          </div>
          <p className="text-sm text-muted-foreground">
            {data.businessType ? businessTypeLabels[data.businessType] || data.businessType : 'Not selected'}
          </p>
        </div>

        {/* Business Details */}
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-medium text-foreground">Business Details</h3>
            <button
              onClick={() => onEdit('business_details')}
              className="flex items-center gap-1 text-sm text-primary hover:underline"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </button>
          </div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <div>
              <span className="text-muted-foreground">Legal Name:</span>{' '}
              <span className="font-medium text-foreground">{data.legalBusinessName || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">EIN:</span>{' '}
              <span className="font-medium text-foreground">{data.ein || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Industry:</span>{' '}
              <span className="font-medium text-foreground">
                {data.industry === 'Others' && data.otherIndustry
                  ? `Others – ${data.otherIndustry}`
                  : data.industry || '—'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Website:</span>{' '}
              <span className="font-medium text-foreground">{data.businessWebsite || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Phone:</span>{' '}
              <span className="font-medium text-foreground">{formatPhone(data.businessPhone) || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">DBA:</span>{' '}
              <span className="font-medium text-foreground">{data.dbaName || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Business Started:</span>{' '}
              <span className="font-medium text-foreground">{data.businessStartedDate || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Avg. Monthly Sales:</span>{' '}
              <span className="font-medium text-foreground">
                {data.averageMonthlySalesVolume ? `$${Number(data.averageMonthlySalesVolume).toLocaleString()}` : '—'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Avg. Sale Amount:</span>{' '}
              <span className="font-medium text-foreground">
                {data.averageSaleAmountPerTransaction
                  ? `$${Number(data.averageSaleAmountPerTransaction).toLocaleString()}`
                  : '—'}
              </span>
            </div>
            <div className="col-span-1 sm:col-span-2">
              <span className="text-muted-foreground">Address:</span>{' '}
              <span className="font-medium text-foreground">
                {[data.businessStreet, data.businessCity, getStateName(data.businessState), data.businessZip]
                  .filter(Boolean)
                  .join(', ') || '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Banking Details */}
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-medium text-foreground">Banking Details</h3>
            <button
              onClick={() => onEdit('banking')}
              className="flex items-center gap-1 text-sm text-primary hover:underline"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </button>
          </div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <div>
              <span className="text-muted-foreground">Routing Number:</span>{' '}
              <span className="font-medium text-foreground">{data.routingNumber || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Account Number:</span>{' '}
              <span className="font-medium text-foreground">
                {data.accountNumber ? `••••${data.accountNumber.slice(-4)}` : '—'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Bank Name:</span>{' '}
              <span className="font-medium text-foreground">{data.bankName || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Bank Representative:</span>{' '}
              <span className="font-medium text-foreground">{data.bankRepresentativeName || '—'}</span>
            </div>
          </div>
        </div>

        {/* Representative */}
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-medium text-foreground">Business Representative</h3>
            <button
              onClick={() => onEdit('person')}
              className="flex items-center gap-1 text-sm text-primary hover:underline"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </button>
          </div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <div>
              <span className="text-muted-foreground">Name:</span>{' '}
              <span className="font-medium text-foreground">
                {[data.repFirstName, data.repLastName].filter(Boolean).join(' ') || '—'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">DOB:</span>{' '}
              <span className="font-medium text-foreground">{data.repDob || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Email:</span>{' '}
              <span className="font-medium text-foreground">{data.repEmail || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Phone:</span>{' '}
              <span className="font-medium text-foreground">{formatPhone(data.repPhone) || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Job Title:</span>{' '}
              <span className="font-medium text-foreground">{data.repJobTitle || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Ownership Percentage:</span>{' '}
              <span className="font-medium text-foreground">
                {data.ownershipPercentage ? `${data.ownershipPercentage}%` : '—'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">SSN:</span>{' '}
              <span className="font-medium text-foreground">
                {data.repSsnLast4 ? `•••-••-${data.repSsnLast4.slice(-4)}` : '—'}
              </span>
            </div>
            <div className="col-span-1 sm:col-span-2">
              <span className="text-muted-foreground">Address:</span>{' '}
              <span className="font-medium text-foreground">
                {[data.repStreet, data.repCity, getStateName(data.repState), data.repZip].filter(Boolean).join(', ') ||
                  '—'}
              </span>
            </div>
            {(data.repDriverLicenseNumber || data.repDriverLicenseState || data.repDriverLicenseExpiry) && (
              <>
                <div>
                  <span className="text-muted-foreground">DL Number:</span>{' '}
                  <span className="font-medium text-foreground">{data.repDriverLicenseNumber || '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">DL State:</span>{' '}
                  <span className="font-medium text-foreground">{getStateName(data.repDriverLicenseState) || '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">DL Expiry:</span>{' '}
                  <span className="font-medium text-foreground">{data.repDriverLicenseExpiry || '—'}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Wizard ────────────────────────────────────────────────────

export function RegistrationWizard({ locationId }: { locationId?: string } = {}) {
  const { data: onboardingRes, isLoading: onboardingLoading } = useOnboarding(locationId);
  const { data: locationsData, isLoading: locationsLoading } = useLocations();
  const { user, tenant } = useAuthStore();

  if (onboardingLoading || locationsLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Compute initial values from server data (no useEffect needed)
  const savedData = onboardingRes?.data;

  // Find location to pre-populate data captured during Add Company
  const location = locationsData?.data?.find((l: any) => l.id === locationId);

  const nameParts = user?.name?.split(' ') || [];

  // Seed the form differently depending on whether this is:
  //   (a) a new additional company/location  — only use data from Add Company; never bleed
  //       the first business's tenant-level profile into a separate company's application.
  //   (b) the original tenant onboarding     — seed from the tenant/user profile as before.
  // In both cases, already-saved progress for this locationId takes precedence.
  const initialFormData: Partial<OnboardingData> =
    savedData ||
    (locationId
      ? {
          // New company: only seed what was explicitly captured in the Add Company wizard
          businessType: location?.type === 'warehouse' ? ('warehouse' as const) : undefined,
          legalBusinessName: location?.name || '',
          businessStreet: location?.address || '',
          // Rep identity comes from the logged-in user (same person across all companies)
          repFirstName: nameParts[0] || '',
          repLastName: nameParts.slice(1).join(' ') || '',
          repEmail: user?.email || '',
        }
      : {
          // Original tenant onboarding: seed from existing tenant/user profile.
          // Warehouse chosen at signup pre-selects the Warehouse business type.
          businessType: tenant?.type === 'warehouse' ? ('warehouse' as const) : undefined,
          legalBusinessName: tenant?.name || '',
          businessPhone: tenant?.phone || '',
          businessStreet: tenant?.street || '',
          businessCity: tenant?.city || '',
          businessState: tenant?.state || '',
          businessZip: tenant?.zip || '',
          businessCountry: tenant?.country || 'United States',
          repFirstName: nameParts[0] || '',
          repLastName: nameParts.slice(1).join(' ') || '',
          repEmail: user?.email || '',
          repPhone: tenant?.phone || '',
          repCountry: tenant?.country || 'United States',
        });

  // Always start from business_type when there is no previously saved progress
  const initialStep: StepKey =
    savedData?.currentStep && STEP_ORDER.includes(savedData.currentStep as StepKey)
      ? (savedData.currentStep as StepKey)
      : 'business_type';

  return <RegistrationWizardForm initialFormData={initialFormData} initialStep={initialStep} locationId={locationId} />;
}

function RegistrationWizardForm({
  initialFormData,
  initialStep,
  locationId,
}: {
  initialFormData: Partial<OnboardingData>;
  initialStep: StepKey;
  locationId?: string;
}) {
  const saveOnboarding = useSaveOnboarding(locationId);
  const submitOnboarding = useSubmitOnboarding(locationId);
  const { user, tenant, setAuth } = useAuthStore();

  const [currentStep, setCurrentStep] = useState<StepKey>(initialStep);
  const [formData, setFormData] = useState<Partial<OnboardingData>>(initialFormData);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const handleFieldChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear the field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step: StepKey): FieldErrors => {
    const errors: FieldErrors = {};
    switch (step) {
      case 'business_type':
        if (!formData.businessType) errors._toast = 'Please select a business type';
        break;
      case 'business_details':
        if (!formData.legalBusinessName?.trim()) errors.legalBusinessName = 'Legal business name is required';
        if (!formData.ein?.trim()) errors.ein = 'EIN is required';
        else if (!/^\d{2}-\d{7}$/.test(formData.ein.trim())) errors.ein = 'EIN must be in XX-XXXXXXX format (9 digits)';
        if (formData.industry === 'Others' && !formData.otherIndustry?.trim()) {
          errors.otherIndustry = 'Please specify your industry';
        }
        {
          const bPhoneErr = validatePhone(formData.businessPhone);
          if (bPhoneErr) errors.businessPhone = bPhoneErr;
        }
        if (formData.businessStartedDate) {
          const today = new Date().toISOString().split('T')[0];
          if (formData.businessStartedDate >= today)
            errors.businessStartedDate = 'Business started date must be a past date';
        }
        if (!formData.averageMonthlySalesVolume?.trim())
          errors.averageMonthlySalesVolume = 'Average monthly sales volume is required';
        else {
          const vol = formData.averageMonthlySalesVolume.trim();
          if (!/^\d+(\.\d+)?$/.test(vol)) errors.averageMonthlySalesVolume = 'Must be a valid number';
          else if (vol.replace('.', '').replace(/^0+/, '').length < 4 && parseFloat(vol) < 1000)
            errors.averageMonthlySalesVolume = 'Must be at least 4 digits';
        }
        if (formData.averageSaleAmountPerTransaction?.trim()) {
          const amt = formData.averageSaleAmountPerTransaction.trim();
          if (!/^\d+(\.\d+)?$/.test(amt)) errors.averageSaleAmountPerTransaction = 'Must be a valid number';
        }
        if (formData.businessWebsite?.trim()) {
          const raw = formData.businessWebsite.trim();
          // Allow bare domains (e.g. example.com, www.example.com) by prepending https://
          const testUrl = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
          try {
            const url = new URL(testUrl);
            if (!['http:', 'https:'].includes(url.protocol) || !url.hostname.includes('.'))
              errors.businessWebsite = 'Please enter a valid URL (e.g. example.com or https://example.com)';
          } catch {
            errors.businessWebsite = 'Please enter a valid URL (e.g. example.com or https://example.com)';
          }
        }
        break;
      case 'banking':
        if (!formData.routingNumber?.trim()) errors.routingNumber = 'Routing number is required';
        else if (!/^\d{9}$/.test(formData.routingNumber.trim())) errors.routingNumber = 'Must be exactly 9 digits';
        if (!formData.accountNumber?.trim()) errors.accountNumber = 'Account number is required';
        else if (!/^\d{6,17}$/.test(formData.accountNumber.trim()))
          errors.accountNumber = 'Must be between 6 and 17 digits';
        if (!formData.bankName?.trim()) errors.bankName = 'Bank name is required';
        else if (formData.bankName.trim().length < 3) errors.bankName = 'Must be at least 3 characters';
        break;
      case 'person':
        if (!formData.repFirstName?.trim()) errors.repFirstName = 'First name is required';
        if (!formData.repLastName?.trim()) errors.repLastName = 'Last name is required';
        if (!formData.repDob) errors.repDob = 'Date of birth is required';
        else {
          const today = new Date();
          const dob = new Date(formData.repDob);
          if (dob >= new Date(today.toISOString().split('T')[0])) errors.repDob = 'Must be a past date';
          else {
            const age =
              today.getFullYear() -
              dob.getFullYear() -
              (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
            if (age < 18) errors.repDob = 'Must be at least 18 years old';
          }
        }
        if (!formData.repEmail?.trim()) errors.repEmail = 'Email is required';
        {
          const phoneErr = validatePhone(formData.repPhone, true);
          if (phoneErr) errors.repPhone = phoneErr;
        }
        if (!formData.repSsnLast4?.trim()) errors.repSsnLast4 = 'SSN Number is required';
        else if (!/^\d{3}-\d{2}-\d{4}$/.test(formData.repSsnLast4.trim()))
          errors.repSsnLast4 = 'Must be in XXX-XX-XXXX format';
        {
          const dlError = validateDriversLicense(formData.repDriverLicenseNumber);
          if (dlError) errors.repDriverLicenseNumber = dlError;
        }
        if (formData.repDriverLicenseExpiry?.trim()) {
          const today = new Date().toISOString().split('T')[0];
          if (formData.repDriverLicenseExpiry < today)
            errors.repDriverLicenseExpiry = 'Must be a present or future date';
        }
        break;
    }
    return errors;
  };

  const handleContinue = async () => {
    setError('');
    setFieldErrors({});

    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      // _toast is used for non-field errors (e.g. business type selection)
      if (errors._toast) setError(errors._toast);
      setFieldErrors(errors);
      return;
    }

    const stepIdx = getStepIndex(currentStep);
    const nextStep = STEP_ORDER[stepIdx + 1];

    // Save progress
    try {
      await saveOnboarding.mutateAsync({
        ...formData,
        currentStep: nextStep,
      });
      setCurrentStep(nextStep);
    } catch (err: any) {
      setError(err?.error?.message || 'Failed to save progress');
    }
  };

  const handleBack = () => {
    setError('');
    setFieldErrors({});
    const stepIdx = getStepIndex(currentStep);
    if (stepIdx > 0) {
      setCurrentStep(STEP_ORDER[stepIdx - 1]);
    }
  };

  const handleSubmit = async () => {
    setError('');
    try {
      await submitOnboarding.mutateAsync();
      // Update auth store to reflect onboardingCompleted
      if (tenant && user) {
        setAuth(user, { ...tenant, onboardingCompleted: true });
      }
      // For location-scoped registration (tenant already active), navigate to home
      if (locationId && tenant?.isActive) {
        window.location.hash = '#/';
      }
    } catch (err: any) {
      setError(err?.error?.message || 'Failed to submit application');
    }
  };

  const handleEditFromReview = (step: StepKey) => {
    setError('');
    setFieldErrors({});
    setCurrentStep(step);
  };

  const currentStepIdx = getStepIndex(currentStep);
  const isLastStep = currentStep === 'review';

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-0 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl font-bold text-foreground sm:text-2xl">Complete Your Business Registration</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Provide your business information to get your account activated.
        </p>
      </div>

      {/* Mobile step indicator */}
      <div className="mb-4 sm:hidden">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            Step {currentStepIdx + 1} of {STEPS.length}: {STEPS[currentStepIdx].label}
          </span>
          <span>{Math.round((currentStepIdx / (STEPS.length - 1)) * 100)}%</span>
        </div>
        <div className="mt-2 flex gap-1.5">
          {STEPS.map((step, idx) => (
            <button
              key={step.key}
              onClick={() => {
                if (idx < currentStepIdx) {
                  setError('');
                  setFieldErrors({});
                  setCurrentStep(step.key);
                }
              }}
              disabled={idx > currentStepIdx}
              className={cn(
                'h-1.5 flex-1 rounded-full transition-all',
                idx < currentStepIdx && 'cursor-pointer bg-green-500',
                idx === currentStepIdx && 'bg-primary',
                idx > currentStepIdx && 'bg-muted',
              )}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-6 sm:flex-row sm:gap-8">
        {/* Left sidebar — Step progress (hidden on mobile) */}
        <div className="hidden w-56 flex-shrink-0 sm:block">
          <nav className="space-y-1">
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isActive = step.key === currentStep;
              const isCompleted = idx < currentStepIdx;

              return (
                <button
                  key={step.key}
                  onClick={() => {
                    if (isCompleted) {
                      setError('');
                      setCurrentStep(step.key);
                    }
                  }}
                  disabled={!isCompleted && !isActive}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-all',
                    isActive && 'bg-primary/10 font-medium text-primary',
                    isCompleted && 'cursor-pointer text-foreground hover:bg-muted',
                    !isActive && !isCompleted && 'cursor-default text-muted-foreground',
                  )}
                >
                  <div
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-full text-xs',
                      isActive && 'bg-primary text-white',
                      isCompleted && 'bg-green-100 text-green-700',
                      !isActive && !isCompleted && 'bg-muted text-muted-foreground',
                    )}
                  >
                    {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-3.5 w-3.5" />}
                  </div>
                  {step.label}
                </button>
              );
            })}
          </nav>

          {/* Progress bar */}
          <div className="mt-6 px-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Progress</span>
              <span>{Math.round((currentStepIdx / (STEPS.length - 1)) * 100)}%</span>
            </div>
            <div className="mt-1.5 h-1.5 w-full rounded-full bg-muted">
              <div
                className="h-1.5 rounded-full bg-primary transition-all"
                style={{ width: `${(currentStepIdx / (STEPS.length - 1)) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Right content — Step form */}
        <div className="min-w-0 flex-1">
          <div className="rounded-xl border border-border bg-white p-4 sm:p-6">
            {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

            {currentStep === 'business_type' && <BusinessTypeStep data={formData} onChange={handleFieldChange} />}
            {currentStep === 'business_details' && (
              <BusinessDetailsStep data={formData} onChange={handleFieldChange} fieldErrors={fieldErrors} />
            )}
            {currentStep === 'banking' && (
              <BankingDetailsStep data={formData} onChange={handleFieldChange} fieldErrors={fieldErrors} />
            )}
            {currentStep === 'person' && (
              <PersonStep data={formData} onChange={handleFieldChange} fieldErrors={fieldErrors} />
            )}
            {currentStep === 'review' && <ReviewStep data={formData} onEdit={handleEditFromReview} />}

            {/* Navigation buttons */}
            <div className="mt-6 flex justify-between border-t border-border pt-4">
              <div>
                {currentStepIdx > 0 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex h-10 items-center gap-2 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>
                )}
              </div>
              <div>
                {isLastStep ? (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitOnboarding.isPending}
                    className="flex h-10 items-center gap-2 rounded-lg bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {submitOnboarding.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4" />
                        Submit Application
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleContinue}
                    disabled={saveOnboarding.isPending}
                    className="flex h-10 items-center gap-2 rounded-lg bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {saveOnboarding.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
